import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Plus, Minus, HelpCircle } from "lucide-react";

const FAQ_ITEMS = [
  {
    q: "Quel est le prix d'un ravalement de façade dans le Nord ?",
    a: "Le tarif d'un ravalement de façade complet à Valenciennes et dans le Nord (59) se situe entre 70 et 110 €/m² selon l'état du support, la finition choisie (enduit projeté gratté fin, taloché, peinture façade…) et la nécessité d'une sous-couche armée avec trame fibre. Notre estimateur en ligne vous donne une fourchette précise en moins d'une minute.",
  },
  {
    q: "Quand faut-il rénover sa façade ?",
    a: "Une façade doit être rénovée tous les 10 à 15 ans en moyenne. Les signes qui indiquent qu'il est temps d'agir : fissures, mousses et lichens, peinture qui s'écaille, joints de briques abîmés, infiltrations d'humidité, salissures persistantes. Le ravalement est aussi obligatoire tous les 10 ans dans certaines communes du Nord. Notre Diagnostic IA gratuit vous donne un avis objectif à partir d'une simple photo.",
  },
  {
    q: "Quelle est la différence entre enduit projeté et peinture façade ?",
    a: "L'enduit projeté est appliqué en couche épaisse (5 à 15 mm) avec une machine pneumatique : il imperméabilise, masque les défauts du support et offre une vraie texture (gratté blanc cassé, ton pierre, anthracite, etc.). La peinture façade, plus économique, se contente de recouvrir l'existant en fine couche. Pour une rénovation durable dans le Nord (climat humide), l'enduit projeté est largement recommandé.",
  },
  {
    q: "Qu'est-ce que le rejointoiement de briques ?",
    a: "Le rejointoiement consiste à enlever les joints de mortier dégradés entre les briques d'une façade et à les remplacer par un mortier neuf de la bonne teinte (chaux, ciment ou mixte). C'est une spécialité très demandée dans le Nord et le Valenciennois où les maisons en brique ancienne sont nombreuses. Le rejointoiement redonne 30 ans de vie à une façade et améliore considérablement son étanchéité.",
  },
  {
    q: "Combien de temps dure un chantier de rénovation de façade ?",
    a: "Pour une maison individuelle d'environ 120 m² de façade, comptez en moyenne 5 à 10 jours ouvrés selon les prestations : 2 à 3 jours pour un simple nettoyage + peinture, 5 à 7 jours pour un enduit projeté complet, 8 à 12 jours pour un ravalement avec sous-couche armée et finition pierre de taille. Nous respectons strictement le planning convenu au devis.",
  },
  {
    q: "Quelles aides financières existent pour rénover sa façade en 2026 ?",
    a: "Plusieurs aides existent : TVA réduite à 10 % pour les travaux d'entretien et de rénovation, MaPrimeRénov' si vous couplez le ravalement avec une isolation thermique extérieure (ITE), éco-prêt à taux zéro, aides locales du département du Nord et de certaines communes. Nous vous accompagnons dans le montage des dossiers et le devis détaillé éligible.",
  },
  {
    q: "Intervenez-vous en dehors de Valenciennes ?",
    a: "Oui, GMC Rénovation Façade intervient dans tout le département du Nord (59) : Valenciennes, Anzin, Saint-Saulve, Marly, Denain, Bruay-sur-l'Escaut, Condé-sur-l'Escaut, Onnaing, Vieux-Condé, Raismes, Aulnoy-lez-Valenciennes, Famars, Maing, Trith-Saint-Léger… Notre flotte de 4 véhicules nous permet d'intervenir rapidement dans tout le Valenciennois et au-delà.",
  },
  {
    q: "Êtes-vous assurés ? Avez-vous la garantie décennale ?",
    a: "Oui, GMC Rénovation Façade est intégralement couverte par une assurance garantie décennale qui protège tous les travaux pendant 10 ans. Nous fournissons systématiquement l'attestation à la signature du devis. C'est une obligation légale que nous prenons très au sérieux pour la tranquillité de nos clients.",
  },
];

export default function FAQ() {
  const [open, setOpen] = useState(0);

  // Inject JSON-LD FAQPage schema into <head> for SEO rich snippets.
  // We use a real <script> element (not dangerouslySetInnerHTML) — content
  // comes from the static FAQ_ITEMS array so there is no XSS surface.
  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQ_ITEMS.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    });
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="relative py-24 md:py-32 bg-[#050505] border-y border-white/5"
    >
      <div
        className="absolute inset-0 opacity-25 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 70% 30%, rgba(255,90,0,0.12) 0%, transparent 55%)",
        }}
      />
      <div className="relative max-w-5xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="mb-12 text-center max-w-3xl mx-auto"
        >
          <div className="eyebrow mb-4 inline-flex items-center gap-2">
            <HelpCircle size={12} />
            <span>Foire aux questions</span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light leading-tight mb-4">
            Vos questions sur
            <br />
            <em className="text-[#FF5A00] not-italic font-normal">la rénovation façade</em>
          </h2>
          <p className="text-neutral-400 text-base md:text-lg">
            Prix, délais, aides, garanties — toutes les réponses pour bien démarrer votre projet.
          </p>
        </motion.div>

        <div className="space-y-3" data-testid="faq-list">
          {FAQ_ITEMS.map((item, i) => {
            const isOpen = open === i;
            return (
              <motion.div
                key={item.q}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                className={`glass rounded-2xl overflow-hidden transition-colors ${
                  isOpen ? "border-[#FF5A00]/40" : "border-white/10"
                }`}
              >
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  data-testid={`faq-toggle-${i}`}
                  aria-expanded={isOpen}
                  className="w-full p-5 md:p-6 flex items-center justify-between gap-4 text-left hover:bg-white/[0.02] transition-colors"
                >
                  <h3 className="font-display text-lg md:text-xl text-white leading-snug">
                    {item.q}
                  </h3>
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-all ${
                      isOpen
                        ? "bg-[#FF5A00] text-white"
                        : "bg-white/5 text-neutral-400"
                    }`}
                  >
                    {isOpen ? <Minus size={14} /> : <Plus size={14} />}
                  </div>
                </button>
                {isOpen && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    transition={{ duration: 0.3 }}
                    className="px-5 md:px-6 pb-6 text-neutral-300 leading-relaxed text-sm md:text-base"
                    data-testid={`faq-answer-${i}`}
                  >
                    {item.a}
                  </motion.div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
